import axios from "axios";
import clsx from "clsx";
import EmojiPicker from 'emoji-picker-react';
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { useAlert } from "react-alert";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "@apollo/client";
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import { Box, Button, Icon, Popper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ADD_MESSAGE as ADD_DIRECT_MESSAGE } from "../../../graphql/mutations/DirectMessageContext";
import { ADD_MESSAGE as ADD_SITE_CHANNEL_MESSAGE } from "../../../graphql/mutations/SiteChannelContext";
import { INIT_ATTACHMENT_UPLOAD as INIT_DIRECT_MESSAGE_ATTACHMENT_UPLOAD } from "../../../graphql/mutations/DirectMessageContext";
import { INIT_ATTACHMENT_UPLOAD as INIT_SITE_CHANNEL_ATTACHMENT_UPLOAD } from "../../../graphql/mutations/SiteChannelContext";
import logger from "../../../helpers/logger";

import SelectFileFromSystemModal from "../../common/SelectFileFromSystemModal";
import MessageBoxAttachments from "./MessageBoxAttachments";
import UploadPopover from "./UploadPopover";
import { ChatMessageInput } from "../../common/Mentions/MentionEditor";

const useStyles = makeStyles(theme => ({
  iconLink: {
    color: "#888",
    marginLeft: 10,
    padding: 0,
    minWidth: "unset",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "transparent",
      opacity: 0.8,
    },
  },
  icon: {
    width: "unset",
    height: "unset",
    overflow: "unset",
    fontSize: 14,
    marginRight: 5,
  },
  messageField: {
    marginTop: 0,
    borderRadius: 4,
    flexGrow: 1,
    backgroundColor: theme.palette.common.white,
    "& .MuiOutlinedInput-input": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiInputBase-root": {
      padding: 0,
    },
  },
  messageBox: {
    width: "100%",
  },
  iconSendLink: {
    color: theme.palette.primary.main,
    padding: 10,
    borderRadius: 0,
    borderLeft: "1px solid #dedede",
    marginLeft: 10,
    minWidth: "unset",
    "&:hover": {
      cursor: "pointer",
      backgroundColor: "transparent",
      opacity: 0.8,
    },
  },
  iconSend: {
    width: "unset",
    height: "unset",
    overflow: "unset",
    fontSize: 16,
  },
  root: {
    borderRadius: 6,
    border: "1px solid #E4E4E4",
    backgroundColor: theme.palette.common.white,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
}));

const MessageBox = ({
  activeChannelId,
  activeConversationId,
  disabled,
  mentionSuggestions = []
}) => {
  const alert = useAlert();
  const classes = useStyles();

  const editorRef = useRef(null);
  const intl = useIntl();

  const [anchorEl, setAnchorEl] = useState(null);

  const uploadPopoverVisible = Boolean(anchorEl);
  const uploadPopoverId = uploadPopoverVisible ? "upload-popover" : undefined;

  const [attachments, setAttachments] = useState([]);
  const [emoticonAnchorEl, setEmoticonAnchorEl] = useState(null);
  const [hideComponent, setHideComponent] = useState(true);
  const [messageText, setMessageText] = useState("");
  const [showEmoticonPopper, setShowEmoticonPopper] = useState(false);
  const [toUploadFiles, setToUploadFiles] = useState([]);
  const [toUploadFromSystem, setToUploadFromSystem] = useState([]);
  const [uploadFromSystemModal, setUploadFromSystemModal] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const [addMessage, { loading: loadingAddMessage }] = useMutation(ADD_SITE_CHANNEL_MESSAGE);
  const [sendDirectMessage, { loading: loadingSendDirectMessage }] = useMutation(ADD_DIRECT_MESSAGE);
  const [initDirectMessageAttachmentUpload] = useMutation(INIT_DIRECT_MESSAGE_ATTACHMENT_UPLOAD);
  const [initSiteChannelAttachmentUpload] = useMutation(INIT_SITE_CHANNEL_ATTACHMENT_UPLOAD);

  useEffect(() => {
    setHideComponent(Boolean(activeChannelId) === Boolean(activeConversationId));
  }, [activeChannelId, activeConversationId]);

  if (hideComponent) {
    return null;
  }

  const haveAttachments = (attachments.length > 0);

  const isEmptyText = (inputText) => {
    const text = (inputText || "").trim();
    return (text.length === 0 || text === "<p></p>");
  };

  const resetMessageState = () => {
    setAttachments([]);
    setMessageText("<p></p>");
    editorRef?.current.clear();
  };

  const handleOpenPopover = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleEmoticonOpen = (event) => {
    if (!showEmoticonPopper) {
      setEmoticonAnchorEl(event.currentTarget);
      setShowEmoticonPopper(true);
    }
    else {
      setEmoticonAnchorEl(null);
      setShowEmoticonPopper(false);
    }
  }

  const handleCloseuploadFromSystemModal = () => {
    setToUploadFiles([...toUploadFiles, ...toUploadFromSystem]);
    setUploadFromSystemModal(false);
    setToUploadFromSystem([]);
  };

  const handleOpenuploadFromSystemModal = () => {
    handleClosePopover();
    setUploadFromSystemModal(true);
    setToUploadFromSystem([]);
  };

  const handleUploadFilesFromSystem = files => {
    setToUploadFromSystem([...files]);
  };

  const handleAddMessage = async () => {
    if (isEmptyText(messageText) && !haveAttachments) {
      return;
    }

    try {
      const message = {
        content: [{ content: messageText, type: "TEXT" }],
        siteChannelId: activeChannelId,
        attachments: attachments.filter(x => x.uploaded).map(x => x.id),
      };

      const results = await addMessage({
        variables: {
          message,
        },
      });

      if (
        results &&
        results.data &&
        results.data.siteChannelContext &&
        results.data.siteChannelContext.addMessage
      ) {
        resetMessageState();
      } else {
        alert.error(<FormattedMessage id="messaging.errors.failedToSend" />);
      }
    } catch (e) {
      alert.error(<FormattedMessage id="messaging.errors.failedToSend" />);
    }
  };

  const handleSendDirectMessage = async () => {
    if (isEmptyText(messageText) && !haveAttachments) {
      return;
    }

    try {
      const message = {
        content: [{ content: messageText, type: "TEXT" }],
        directConversationId: activeConversationId,
        attachments: attachments.filter(x => x.uploaded).map(x => x.id),
      };

      const results = await sendDirectMessage({
        variables: {
          message,
        },
      });

      if (
        results &&
        results.data &&
        results.data.directMessageContext &&
        results.data.directMessageContext.addMessage
      ) {
        resetMessageState();
      } else {
        alert.error(<FormattedMessage id="messaging.errors.failedToSend" />);
      }
    } catch (e) {
      alert.error(<FormattedMessage id="messaging.errors.failedToSend" />);
    }
  };

  const sendMessage = () => {
    if (Boolean(activeChannelId)) {
      handleAddMessage();
    } else if (Boolean(activeConversationId)) {
      handleSendDirectMessage();
    }
  };

  const handleInitAttachmentUpload = async () => {
    if (Boolean(activeChannelId)) {
      return await initSiteChannelAttachmentUpload({
        variables: { siteChannelId: activeChannelId },
      });
    } else if (Boolean(activeConversationId)) {
      return await initDirectMessageAttachmentUpload({
        variables: { directMessageConversationId: activeConversationId },
      });
    } else {
      throw new Error("Invalid state.");
    }
  };

  const getInitAttachmentUploadResult = res => {
    var { directMessageContext, siteChannelContext } = res?.data || {};
    if (Boolean(activeChannelId)) {
      return siteChannelContext?.initAttachmentUpload || {};
    } else if (Boolean(activeConversationId)) {
      return directMessageContext?.initAttachmentUpload || {};
    } else {
      throw new Error("Invalid state.");
    }
  };

  const createExceptionMessage = errorResult => {
    return errorResult
      ? `${errorResult.errorCode} : ${errorResult.errorMessage}`
      : "unknown error";
  };

  const handleAttachmentUpload = async e => {
    const { target } = e || {};
    const { files } = target || {};

    handleClosePopover();

    const file = files[0];
    if (!file) {
      logger.info("No attachment provided for upload.");
      return;
    }

    setUploadingAttachment(true);

    var uploadUrl;

    try {
      const res = await handleInitAttachmentUpload();
      const initResult = getInitAttachmentUploadResult(res);
      if (!initResult.success) {
        throw new Error(createExceptionMessage(initResult.errorResult));
      }
      uploadUrl = initResult.url;
    } catch (ex) {
      logger.error("Failed to initialize upload.", ex);
      alert.error(<FormattedMessage id="messaging.errors.failedToUpload" />);
      setUploadingAttachment(false);
      return;
    }

    setAttachments([
      ...attachments,
      {
        id: null,
        key: uploadUrl,
        name: file.name,
        size: file.size,
        type: file.type,
        uploaded: false,
        url: URL.createObjectURL(file),
      },
    ]);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await axios.post(`${uploadUrl}`, formData);

      if (response && response.data && response.status === 200) {
        const { data } = response;
        if (!data.success) {
          throw new Error(createExceptionMessage(data.errorResult));
        }
        updateOrAddAttachment(uploadUrl, data);
      } else {
        throw new Error(createExceptionMessage(response?.data?.errorResult));
      }

      logger.info("Attachment added.");
    } catch (ex) {
      logger.error("Failed to upload attachment.", ex);
      alert.error(<FormattedMessage id="messaging.errors.failedToUpload" />);
      removeAttachment(uploadUrl);
    }

    setUploadingAttachment(false);
  };

  const removeAttachment = key => {
    const temp = attachments.filter((x, idx) => x.key !== key);
    setAttachments([...temp]);
  };

  const updateOrAddAttachment = (key, data) => {
    var temp = [...attachments];
    var o = temp.find(n => n.key === key);
    if (o) {
      o.id = data.id;
      o.uploaded = true;
      o.url = data.previewUrl;
      setAttachments(temp);
    } else {
      setAttachments([
        ...attachments,
        {
          id: data.id,
          key: data.id,
          name: data.filename,
          size: data.size,
          type: data.mediaType,
          uploaded: true,
          url: data.previewUrl,
        },
      ]);
    }
  };

  const handleEmojiSelect = (emojiObject) => {
    if (editorRef.current) {
      editorRef.current?.insertEmoticon(emojiObject.emoji);
    }

    setShowEmoticonPopper(false);
  };

  return (
    <Box className={classes.root}>
      {/* Attachments preview */}
      <MessageBoxAttachments
        attachments={attachments}
        removeAttachment={removeAttachment}
      />

      {/* Chat input + buttons */}
      <Box className={classes.messageBox} sx={{ position: "relative" }}>
        {/* Editor */}
        
        <ChatMessageInput
          ref={editorRef}
          disabled={disabled || loadingAddMessage || loadingSendDirectMessage || uploadingAttachment}
          disableMentions
          disableToolbar
          clearOnSubmit={false}
          onBlur={setMessageText}
          onChange={setMessageText}
          onEnterPressed={sendMessage}
          placeholder={intl.formatMessage({ id: "messaging.messageTextInput.placeholder" })}
          minHeight={50}
        />

        {/* Emoji picker popper */}
        <Popper open={showEmoticonPopper} anchorEl={emoticonAnchorEl}>
          <EmojiPicker
            onEmojiClick={handleEmojiSelect}
            emojiStyle="native"
            emojiVersion="4.0"
          />
        </Popper>

        {/* Buttons container (absolute positioned at bottom-right) */}
        <Box sx={{ position: "absolute", bottom: 8, right: 8, display: 'flex', gap: 4 }}>
          {/* Emoji button */}
          <Button
            className={classes.iconLink}
            onClick={handleEmoticonOpen}
            disabled={disabled || loadingAddMessage || loadingSendDirectMessage || uploadingAttachment}
          >
            <InsertEmoticonIcon />
          </Button>

          {/* Attach button */}
          <Button
            className={classes.iconLink}
            onClick={handleOpenPopover}
            disabled={disabled || loadingAddMessage || loadingSendDirectMessage || uploadingAttachment}
          >
            <Icon className={clsx(["fas fa-paperclip", classes.icon])} />
          </Button>

          {/* Send button */}
          <Button
            title={<FormattedMessage id="messaging.sendMessageButton.title" />}
            className={classes.iconSendLink}
            onClick={sendMessage}
            disabled={disabled || loadingAddMessage || loadingSendDirectMessage || uploadingAttachment}
          >
            <Icon className={clsx(["fas fa-paper-plane", classes.iconSend])} />
          </Button>
        </Box>
      </Box>

      {/* Upload popover + file selection modal */}
      <UploadPopover
        id={uploadPopoverId}
        open={uploadPopoverVisible}
        anchorEl={anchorEl}
        handleClose={handleClosePopover}
        handleOnSelectUploadFile={handleAttachmentUpload}
        handleOpenuploadFromSystemModal={handleOpenuploadFromSystemModal}
      />
      <SelectFileFromSystemModal
        open={uploadFromSystemModal}
        handleClose={handleCloseuploadFromSystemModal}
        setSelectedResources={handleUploadFilesFromSystem}
        selectedResources={toUploadFromSystem}
      />
    </Box>
  );
};

MessageBox.defaultProps = {
  activeChannelId: null,
  activeConversationId: null,
  disabled: false,
  mentionSuggestions: [],
};

MessageBox.propTypes = {
  activeChannelId: PropTypes.number,
  activeConversationId: PropTypes.number,
  disabled: PropTypes.bool,
  mentionSuggestions: PropTypes.arrayOf(PropTypes.shape({})),
};

export default MessageBox;
